import { useIntl } from 'react-intl';
import { useMemo, useCallback, useState, useEffect } from 'react';
import { DetailedDeliveryForm } from '../../Form/Listings/DeliveryConditionForms/DetailedDeliveryForm';
import { Button } from '../../Button';
import { Icon } from '../../Icon';
import {
  SplitDeliveryData,
  SplitDeliveriesMode,
  SplitDeliveryListingProduct,
  SplitDeliveryProduct,
  ListingProduct,
  DetailedDeliveryData,
  DeliveryProductReference,
} from '../../../types/ListingData';
import { StyledDrawer, StyledFooter } from './style';

interface ExtendedApplication {
  id: number;
  farm_name?: string;
  farm_brand?: string;
  order_transaction?: unknown;
  application_products?: Array<{
    id?: number | null;
    offered_price?: number;
    listing_product?: ListingProduct;
  }>;
}

interface DrawerSplitDeliveriesProps {
  open: boolean;
  onClose?: () => void;
  selectedProducts: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>;
  applications: ExtendedApplication[];
  listing?: { id: string } | null;
  existingDeliveries?: SplitDeliveryData[];
  onSplitDeliveriesSave?: (splitDeliveries: SplitDeliveryData[]) => void;
}

export const DrawerSplitDeliveries = ({
  open,
  onClose,
  selectedProducts = [],
  applications = [],
  listing,
  existingDeliveries = [],
  onSplitDeliveriesSave,
}: DrawerSplitDeliveriesProps) => {
  const intl = useIntl();
  const [deliveries, setDeliveries] = useState<DetailedDeliveryData[]>([]);
  const [isFormBusy, setIsFormBusy] = useState(false);

  useEffect(() => {
    if (open && existingDeliveries.length > 0) {
      const detailedDeliveries: DetailedDeliveryData[] = existingDeliveries.map(
        (splitDelivery, index) => {
          const deliveryProductReferences: DeliveryProductReference[] = [];
          const selectedProducts: ListingProduct[] = [];
          const productQuantities: { [productId: string]: number } = {};

          splitDelivery.products.forEach((product) => {
            const application = applications.find(
              (app) => app.id === product.application_id
            );
            const applicationProduct = application?.application_products?.find(
              (ap) => ap.id === product.application_product_id
            );

            if (applicationProduct?.listing_product) {
              const listingProductId = applicationProduct.listing_product.id;
              if (listingProductId) {
                deliveryProductReferences.push({
                  listing_product_id: listingProductId,
                  category_id: applicationProduct.listing_product.category_id,
                  delivery_quantity: product.quantity,
                });

                selectedProducts.push(applicationProduct.listing_product);
                productQuantities[listingProductId] = product.quantity;
              }
            }
          });

          return {
            id: index + 1,
            delivery_address: splitDelivery.delivery_address,
            delivery_start_date: splitDelivery.delivery_start_date,
            delivery_end_date: splitDelivery.delivery_end_date,
            delivery_frequency_value: splitDelivery.delivery_frequency_value,
            delivery_frequency_unit: splitDelivery.delivery_frequency_unit,
            is_all_products: false,
            is_full_quantity: false,
            selected_products: selectedProducts,
            product_quantities: productQuantities,
            products: deliveryProductReferences,
            selectedApplicationId:
              splitDelivery.products.length > 0
                ? splitDelivery.products[0].application_id
                : null,
          };
        }
      );

      setDeliveries(detailedDeliveries);
    } else if (open && existingDeliveries.length === 0) {
      setDeliveries([]);
    }
  }, [open, existingDeliveries, applications]);

  const transformedProducts = useMemo(() => {
    const productGroups = new Map<
      string,
      {
        listingProductId: string;
        totalQuantity: number;
        category_id: number;
        name: string;
        storage_unit?: {
          id: number;
          name: string;
          short?: string;
        };
        category?: {
          id: number;
          name: string;
          route_path: {
            primary?: number;
            secondary?: number;
            tertiary?: number;
          };
        };
        applications: Array<{
          applicationId: number;
          applicationProductId: number;
          quantity: number;
          offeredPrice: number;
          farmName?: string;
          applicationName?: string;
        }>;
      }
    >();

    selectedProducts.forEach((selected) => {
      const application = applications.find(
        (app) => app.id === selected.listing_application_id
      );
      const applicationProduct = application?.application_products?.find(
        (product) => product.id === selected.listing_application_product_id
      );

      if (!applicationProduct?.listing_product) {
        return;
      }

      const listingProduct = applicationProduct.listing_product;
      const listingProductId =
        listingProduct.id || `listing-${listingProduct.category_id}`;

      if (!productGroups.has(listingProductId)) {
        productGroups.set(listingProductId, {
          listingProductId,
          totalQuantity: 0,
          category_id: listingProduct.category_id || 0,
          name: listingProduct.name || '',
          storage_unit: listingProduct.storage_unit,
          category: listingProduct.category,
          applications: [],
        });
      }

      const group = productGroups.get(listingProductId)!;
      group.totalQuantity += selected.selected_quantity;
      group.applications.push({
        applicationId: selected.listing_application_id,
        applicationProductId: selected.listing_application_product_id,
        quantity: selected.selected_quantity,
        offeredPrice: applicationProduct.offered_price || 0,
        farmName: application?.farm_name,
        applicationName: application?.farm_brand || application?.farm_name,
      });
    });

    const result = Array.from(productGroups.values()).map(
      (group) =>
        ({
          id: group.listingProductId,
          category_id: group.category_id,
          name: group.name,
          required_quantity: group.totalQuantity,
          storage_unit: group.storage_unit,
          category: group.category,
          _applications: group.applications,
          _totalQuantity: group.totalQuantity,
        }) as SplitDeliveryListingProduct & {
          _applications: Array<{
            applicationId: number;
            applicationProductId: number;
            quantity: number;
            offeredPrice: number;
            farmName?: string;
            applicationName?: string;
          }>;
          _totalQuantity: number;
        }
    );
    return result;
  }, [selectedProducts, applications]);

  const availableApplications = useMemo(() => {
    const selectedApplicationIds = new Set(
      selectedProducts
        .filter((selected) => {
          const app = applications.find(
            (a) => a.id === selected.listing_application_id
          );
          return app && !app.order_transaction;
        })
        .map((selected) => selected.listing_application_id)
    );

    return Array.from(selectedApplicationIds)
      .map((appId) => {
        const app = applications.find((a) => a.id === appId);
        return app
          ? {
              id: app.id,
              name: app.farm_brand || app.farm_name || `Application ${app.id}`,
              farm_name: app.farm_name,
            }
          : null;
      })
      .filter((app): app is NonNullable<typeof app> => app !== null);
  }, [applications, selectedProducts]);

  const handleDeliveriesChange = useCallback(
    (updatedDeliveries: DetailedDeliveryData[]) => {
      setDeliveries(updatedDeliveries);
    },
    []
  );

  const handleFormStateChange = useCallback((isBusy: boolean) => {
    setIsFormBusy(isBusy);
  }, []);

  const handleClose = useCallback(() => {
    try {
      const splitDeliveries: SplitDeliveryData[] = deliveries.map(
        (delivery, index) => {
          const products: SplitDeliveryProduct[] = [];

          delivery.products.forEach((productRef: DeliveryProductReference) => {
            const formProduct = transformedProducts.find(
              (p) =>
                p.id === productRef.listing_product_id ||
                (productRef.category_id &&
                  p.category_id === productRef.category_id)
            );

            if (!formProduct) return;

            const extendedProduct = formProduct as SplitDeliveryListingProduct;
            const requestedQuantity =
              typeof productRef.delivery_quantity === 'string'
                ? parseFloat(productRef.delivery_quantity)
                : productRef.delivery_quantity || 0;

            if (requestedQuantity <= 0) return;

            const selectedApplicationId = (delivery as DetailedDeliveryData)
              .selectedApplicationId;
            const selectedApplication = extendedProduct._applications?.find(
              (app: {
                applicationId: number;
                applicationProductId: number;
                quantity: number;
                offeredPrice: number;
                farmName?: string;
                applicationName?: string;
              }) => app.applicationId === selectedApplicationId
            );

            if (selectedApplication) {
              products.push({
                application_id: selectedApplication.applicationId,
                application_product_id:
                  selectedApplication.applicationProductId,
                quantity: requestedQuantity,
                offered_price: selectedApplication.offeredPrice,
              });
            }
          });

          return {
            id: delivery.id ? String(delivery.id) : `delivery-${index + 1}`,
            delivery_address: delivery.delivery_address,
            delivery_start_date: delivery.delivery_start_date,
            delivery_end_date: delivery.delivery_end_date,
            delivery_frequency_value: delivery.delivery_frequency_value,
            delivery_frequency_unit: delivery.delivery_frequency_unit,
            products,
          };
        }
      );

      onSplitDeliveriesSave?.(splitDeliveries);
    } catch (error) {}

    onClose?.();
  }, [deliveries, transformedProducts, onSplitDeliveriesSave, onClose]);

  const Footer = () => (
    <StyledFooter>
      <Button
        label={intl.formatMessage({ id: 'general.close' })}
        onClick={handleClose}
        disabled={isFormBusy}
      />
    </StyledFooter>
  );

  const splitDeliveriesMode: SplitDeliveriesMode = {
    enabled: true,
    availableApplications,
    onApplicationChange: (applicationId: number | null) => {},
  };

  return (
    <StyledDrawer
      open={open}
      onClose={handleClose}
      closeIcon={<Icon name={'global-close-icon'} type={'small'} />}
      title={intl.formatMessage({
        id: 'listings.applications.split_deliveries.title',
      })}
      width={1000}
      footer={<Footer />}
    >
      <DetailedDeliveryForm
        availableProducts={transformedProducts as ListingProduct[]}
        onDeliveriesChange={handleDeliveriesChange}
        onFormStateChange={handleFormStateChange}
        existingDeliveries={deliveries}
        splitDeliveriesMode={splitDeliveriesMode}
      />
    </StyledDrawer>
  );
};
